import type { StorefrontPageProps } from '@kitup/brand';
import { useEffect, useRef, type JSX } from 'react';
import { AccountMenu } from './AccountMenu.js';

/**
 * The wordmark in one colour; the two o's are eyes that follow a mouse. They
 * rest centred, looking at the visitor like the square mark, until the mouse
 * moves, and stay that way on touch screens, where there is no pointer to follow.
 *
 * Exported for the app's own screens too (`storefront.logo`): there it renders
 * without a link, since the app decides where its header leads.
 */
export function WordmarkGlyphs(): JSX.Element {
  const markRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const pupils = markRef.current?.querySelectorAll<HTMLElement>('.ta-eye i') ?? [];
    const follow = (event: PointerEvent): void => {
      if (event.pointerType !== 'mouse') return;
      for (const pupil of pupils) {
        const eye = pupil.parentElement!.getBoundingClientRect();
        const dx = event.clientX - (eye.left + eye.width / 2);
        const dy = event.clientY - (eye.top + eye.height / 2);
        const distance = Math.hypot(dx, dy) || 1;
        // The bigger pupil may overlap the ring a little: it is the same navy.
        const reach = Math.min(eye.width * 0.16, distance);
        pupil.style.setProperty('--px', `${(dx / distance) * reach}px`);
        pupil.style.setProperty('--py', `${(dy / distance) * reach}px`);
      }
    };
    addEventListener('pointermove', follow);
    return () => removeEventListener('pointermove', follow);
  }, []);

  return (
    <span ref={markRef} aria-label="TaleAroo" role="img">
      TaleAr
      <span className="ta-eye">
        <i />
      </span>
      <span className="ta-eye">
        <i />
      </span>
    </span>
  );
}

function Wordmark({ Link }: { readonly Link: StorefrontPageProps['Link'] }): JSX.Element {
  return (
    <Link to="/" className="ta-mark">
      <WordmarkGlyphs />
    </Link>
  );
}

export function Header({
  page,
  current,
}: {
  readonly page: StorefrontPageProps;
  readonly current: 'landing' | 'pricing';
}): JSX.Element {
  const { Link, user, authPending, onSignOut } = page;

  return (
    <header>
      <nav className="ta-nav" aria-label="Main">
        <Wordmark Link={Link} />
        <Link to="/pricing" className="ta-nav-link ta-nav-hide">
          <span aria-current={current === 'pricing' ? 'page' : undefined}>Pricing</span>
        </Link>
        {authPending ? null : user ? (
          <AccountMenu user={user} Link={Link} onSignOut={onSignOut} />
        ) : (
          <>
            {/* Stays on phones: a returning visitor should not have to go through sign-up. */}
            <Link to="/login" className="ta-nav-link">
              Sign in
            </Link>
            <Link to="/register" className="ta-btn">
              Create a book
            </Link>
          </>
        )}
      </nav>
    </header>
  );
}

export function Footer({ page }: { readonly page: StorefrontPageProps }): JSX.Element {
  return <footer className="ta-footer">{page.footer}</footer>;
}
